/**
 * iOS App Store account deletion flow (status, reasons, submit, reactivate).
 * Used from Settings on native iOS/iPad.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { useAuthStore } from '@/stores/authStore';
import {
  AccountDeletionService,
  type AccountDeletionReasonsData,
  type AccountDeletionStatusData,
} from '@/services/accountDeletionService';
import { Sheet } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui';

function formatSubmittedDate(dateStr: string | null | undefined): string | null {
  if (!dateStr?.trim()) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function parsePositiveId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'string' ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

function resolveArtistIdForDeletionSubmit(
  reasons: AccountDeletionReasonsData | null,
  user: { id?: string } | null | undefined,
  details: { artist_id?: unknown; id?: unknown } | null
): number | null {
  const fromReasons = parsePositiveId(reasons?.artist_id);
  if (fromReasons != null) return fromReasons;
  const fromUser = parsePositiveId(user?.id);
  if (fromUser != null) return fromUser;
  if (details) {
    const fromDetails =
      parsePositiveId(details.artist_id) ?? parsePositiveId(details.id);
    if (fromDetails != null) return fromDetails;
  }
  return null;
}

export type AccountDeletionFlowApi = {
  statusLoading: boolean;
  canSubmitDeletion: boolean;
  hasPendingDeletion: boolean;
  deletionStatus: AccountDeletionStatusData | null;
  openDeleteFlow: () => void;
  reactivatingAccount: boolean;
  handleReactivateAccount: () => void;
};

type AccountDeletionContextValue = AccountDeletionFlowApi & {
  reasonSheetOpen: boolean;
  setReasonSheetOpen: (open: boolean) => void;
  reasonsData: AccountDeletionReasonsData | null;
  reasonsLoading: boolean;
  selectedReasonId: number | null;
  setSelectedReasonId: (id: number | null) => void;
  submittingDeletion: boolean;
  successModalOpen: boolean;
  handleProceedDeletion: () => void;
  handleSuccessOk: () => void;
  closeReasonSheet: () => void;
};

const AccountDeletionContext = createContext<AccountDeletionContextValue | null>(null);

function useAccountDeletionFlowInternal(enabled: boolean): AccountDeletionContextValue {
  const user = useAuthStore((s) => s.user);
  const { data: artistDetails, fetchArtistDetails } = useArtistDetailsStore();

  const [deletionStatus, setDeletionStatus] = useState<AccountDeletionStatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(enabled);

  const [reasonSheetOpen, setReasonSheetOpen] = useState(false);
  const [reasonsData, setReasonsData] = useState<AccountDeletionReasonsData | null>(null);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [submittingDeletion, setSubmittingDeletion] = useState(false);

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [reactivatingAccount, setReactivatingAccount] = useState(false);

  const refreshArtistDetails = useCallback(() => {
    void fetchArtistDetails();
  }, [fetchArtistDetails]);

  const fetchDeletionStatus = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!enabled) return;
      if (!options?.silent) setStatusLoading(true);
      try {
        const data = await AccountDeletionService.getStatus();
        setDeletionStatus(data);
      } catch {
        if (!options?.silent) {
          toast.error('Could not load account deletion status.');
        }
        setDeletionStatus(null);
      } finally {
        if (!options?.silent) setStatusLoading(false);
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return;
    void fetchDeletionStatus();
  }, [enabled, fetchDeletionStatus]);

  useEffect(() => {
    if (!enabled) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchDeletionStatus({ silent: true });
      }
    };
    const onFocus = () => {
      void fetchDeletionStatus({ silent: true });
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, fetchDeletionStatus]);

  const handleDeleteAccountPress = useCallback(async () => {
    if (!enabled) return;
    setReasonsLoading(true);
    setSelectedReasonId(null);
    setReasonSheetOpen(true);
    try {
      const data = await AccountDeletionService.getReasons();
      setReasonsData(data);
    } catch {
      toast.error('Could not load deletion reasons. Please try again.');
      setReasonSheetOpen(false);
    } finally {
      setReasonsLoading(false);
    }
  }, [enabled]);

  const handleProceedDeletion = useCallback(async () => {
    if (selectedReasonId == null) return;

    let artistId = resolveArtistIdForDeletionSubmit(reasonsData, user, artistDetails);
    if (artistId == null) {
      await fetchArtistDetails();
      artistId = resolveArtistIdForDeletionSubmit(
        reasonsData,
        user,
        useArtistDetailsStore.getState().data as { artist_id?: unknown; id?: unknown } | null
      );
    }
    if (artistId == null) {
      try {
        const fresh = await AccountDeletionService.getReasons();
        setReasonsData(fresh);
        artistId = parsePositiveId(fresh.artist_id);
      } catch {
        // handled below
      }
    }
    if (artistId == null) {
      // eslint-disable-next-line no-console
      console.warn('[AccountDeletion] Missing artist_id for POST', {
        reasonsArtistId: reasonsData?.artist_id,
        userId: user?.id,
      });
      toast.error('Unable to verify your account. Please sign in again.');
      return;
    }

    setSubmittingDeletion(true);
    try {
      await AccountDeletionService.submitDeletion(artistId, selectedReasonId);
      setReasonSheetOpen(false);
      setReasonsData(null);
      setSelectedReasonId(null);
      setSuccessModalOpen(true);
    } catch {
      toast.error('Could not submit account deletion. Please try again.');
    } finally {
      setSubmittingDeletion(false);
    }
  }, [selectedReasonId, reasonsData, user, artistDetails, fetchArtistDetails]);

  const handleSuccessOk = useCallback(() => {
    setSuccessModalOpen(false);
    void fetchDeletionStatus({ silent: true });
    void refreshArtistDetails();
  }, [fetchDeletionStatus, refreshArtistDetails]);

  const handleReactivateAccount = useCallback(async () => {
    setReactivatingAccount(true);
    try {
      await AccountDeletionService.reactivateAccount();
      toast.success('Your Account has been Reactivated');
      await fetchDeletionStatus({ silent: true });
      void refreshArtistDetails();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[AccountDeletion] Reactivate failed', err);
      toast.error('Could not reactivate your account. Please try again.');
    } finally {
      setReactivatingAccount(false);
    }
  }, [fetchDeletionStatus, refreshArtistDetails]);

  const closeReasonSheet = useCallback(() => {
    if (submittingDeletion) return;
    setReasonSheetOpen(false);
    setReasonsData(null);
    setSelectedReasonId(null);
  }, [submittingDeletion]);

  const canSubmitDeletion = deletionStatus?.can_submit === true;
  const hasPendingDeletion =
    deletionStatus?.has_deletion_request === true && !canSubmitDeletion;

  const openDeleteFlow = useCallback(() => {
    if (!canSubmitDeletion) return;
    void handleDeleteAccountPress();
  }, [canSubmitDeletion, handleDeleteAccountPress]);

  return useMemo(
    () => ({
      statusLoading: enabled ? statusLoading : false,
      canSubmitDeletion: enabled ? canSubmitDeletion : false,
      hasPendingDeletion: enabled ? hasPendingDeletion : false,
      deletionStatus: enabled ? deletionStatus : null,
      openDeleteFlow,
      reactivatingAccount,
      handleReactivateAccount: () => void handleReactivateAccount(),
      reasonSheetOpen,
      setReasonSheetOpen,
      reasonsData,
      reasonsLoading,
      selectedReasonId,
      setSelectedReasonId,
      submittingDeletion,
      successModalOpen,
      handleProceedDeletion: () => void handleProceedDeletion(),
      handleSuccessOk,
      closeReasonSheet,
    }),
    [
      enabled,
      statusLoading,
      canSubmitDeletion,
      hasPendingDeletion,
      deletionStatus,
      openDeleteFlow,
      reactivatingAccount,
      handleReactivateAccount,
      reasonSheetOpen,
      reasonsData,
      reasonsLoading,
      selectedReasonId,
      submittingDeletion,
      successModalOpen,
      handleProceedDeletion,
      handleSuccessOk,
      closeReasonSheet,
    ]
  );
}

export function useAccountDeletionFlow(): AccountDeletionFlowApi {
  const ctx = useContext(AccountDeletionContext);
  if (!ctx) {
    throw new Error('useAccountDeletionFlow must be used within AccountDeletionProvider');
  }
  return {
    statusLoading: ctx.statusLoading,
    canSubmitDeletion: ctx.canSubmitDeletion,
    hasPendingDeletion: ctx.hasPendingDeletion,
    deletionStatus: ctx.deletionStatus,
    openDeleteFlow: ctx.openDeleteFlow,
    reactivatingAccount: ctx.reactivatingAccount,
    handleReactivateAccount: ctx.handleReactivateAccount,
  };
}

export const AccountDeletionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useAccountDeletionFlowInternal(true);
  return (
    <AccountDeletionContext.Provider value={value}>{children}</AccountDeletionContext.Provider>
  );
};

/** Reason sheet + success modal — mount at Settings page root inside provider. */
export const AccountDeletionOverlays: React.FC = () => {
  const ctx = useContext(AccountDeletionContext);
  if (!ctx) return null;

  const {
    reasonSheetOpen,
    setReasonSheetOpen,
    reasonsData,
    reasonsLoading,
    selectedReasonId,
    setSelectedReasonId,
    submittingDeletion,
    successModalOpen,
    handleProceedDeletion,
    handleSuccessOk,
    closeReasonSheet,
  } = ctx;

  return (
    <>
      <Sheet
        open={reasonSheetOpen}
        onOpenChange={(open) => {
          if (!open) closeReasonSheet();
          else setReasonSheetOpen(true);
        }}
        title={reasonsData?.heading ?? 'Reason for Deletion'}
        preventOutsideClose={submittingDeletion}
        preventEscapeClose={submittingDeletion}
      >
        <div className="flex flex-col min-h-0 w-full flex-1 px-0 pb-2">
          {reasonsLoading ? (
            <div className="flex justify-center py-12">
              <Loader size="small" />
            </div>
          ) : (
            <>
              <ul
                className="mb-6 flex max-h-[50vh] w-full flex-col overflow-y-auto rounded-xl border border-[#3A3A3C] bg-[#0B0B0D]/40"
                role="listbox"
                aria-label="Deletion reasons"
              >
                {(reasonsData?.reasons ?? []).map((reason) => {
                  const isSelected = selectedReasonId === reason.id;
                  return (
                    <li key={reason.id} className="border-b border-[#3A3A3C] last:border-b-0">
                      <label
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 transition-colors',
                          isSelected ? 'bg-[#2C2C2E]' : 'hover:bg-[#1C1C1E]'
                        )}
                      >
                        <input
                          type="radio"
                          name="deletion-reason-settings"
                          value={reason.id}
                          checked={isSelected}
                          onChange={() => setSelectedReasonId(reason.id)}
                          className="h-4 w-4 shrink-0 border-[#636366] bg-[#1C1C1E] text-[#E5E5EA] accent-[#8E8E93]"
                          disabled={submittingDeletion}
                        />
                        <span className="min-w-0 flex-1 text-left text-[15px] leading-snug text-[#E5E5EA]">
                          {reason.label}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              <div className="flex w-full flex-row items-stretch gap-3 border-t border-[#3A3A3C] pt-4">
                <button
                  type="button"
                  className={cn(
                    'flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] px-3 text-[15px] font-semibold text-[#E5E5EA]',
                    'hover:bg-[#2C2C2E] active:bg-[#2C2C2E] transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#636366] disabled:opacity-50'
                  )}
                  onClick={closeReasonSheet}
                  disabled={submittingDeletion}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={cn(
                    'flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-2xl border border-[#48484A] bg-[#2C2C2E] px-3 text-[15px] font-semibold text-white',
                    'hover:bg-[#3A3A3A] active:bg-[#3A3A3A] transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#636366] disabled:opacity-40',
                    selectedReasonId == null || submittingDeletion ? 'cursor-not-allowed' : ''
                  )}
                  onClick={handleProceedDeletion}
                  disabled={selectedReasonId == null || submittingDeletion}
                >
                  {submittingDeletion ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader size="small" />
                      Processing…
                    </span>
                  ) : (
                    'Proceed'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Sheet>

      {successModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-[#3A3A3C]/50 shadow-xl w-full max-w-sm overflow-hidden bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
            <div className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-white text-center">
                Account Deletion Requested
              </h3>
              <div className="space-y-2 text-[#8E8E93] text-sm text-center leading-relaxed">
                <p>Your account is scheduled for permanent deletion within 7 days.</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="w-full min-h-[48px] rounded-2xl border border-[#48484A] bg-[#2C2C2E] font-bold text-white hover:bg-[#3A3A3A]"
                onClick={handleSuccessOk}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Pending deletion card + Revoke — inside Settings About & Data section. */
export const AccountDeletionPendingInline: React.FC = () => {
  const flow = useAccountDeletionFlow();
  if (!flow.hasPendingDeletion) return null;

  return (
    <div className="px-4 pb-4 space-y-3 border-t border-[#3A3A3C]/50">
      <p className="text-xs font-medium uppercase tracking-wider text-[#8E8E93] pt-3">
        Deletion details
      </p>
      <div className="rounded-xl border border-[#3A3A3C] bg-[#0B0B0D]/60 px-4 py-3">
        <p className="text-sm font-semibold text-white mb-1">Deletion request submitted</p>
        {flow.deletionStatus?.reason_label && (
          <p className="text-sm text-[#8E8E93] mb-1">
            Reason: {flow.deletionStatus.reason_label}
          </p>
        )}
        {flow.deletionStatus?.submitted_at && (
          <p className="text-sm text-[#8E8E93]">
            Submitted on {formatSubmittedDate(flow.deletionStatus.submitted_at)}
          </p>
        )}
        <p className="text-xs text-[#636366] mt-2 leading-relaxed">
          If Cancellation Request is not Revoked within 7 days, Your account will be permanently
          deleted.
        </p>
      </div>
      <button
        type="button"
        onClick={flow.handleReactivateAccount}
        disabled={flow.reactivatingAccount}
        className={cn(
          'w-full flex min-h-[44px] items-center justify-center rounded-xl border border-[#3A3A3C] bg-[#2C2C2E] px-4 py-3',
          'text-sm font-semibold text-white',
          'hover:bg-[#3A3A3A] active:bg-[#3A3A3A] transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#636366]',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        {flow.reactivatingAccount ? (
          <span className="flex items-center justify-center gap-2">
            <Loader size="small" />
            Reactivating…
          </span>
        ) : (
          'Revoke Deletion'
        )}
      </button>
    </div>
  );
};
